import { pricing, contactInfo } from "../data";
import { Check, Phone } from "lucide-react";
import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function TreatmentDetail() {
  const { id } = useParams();
  const category = pricing.find((item) => item.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <h2 className="text-2xl text-stone-900">Behandling ikke fundet</h2>
          <Link to="/" className="mt-4 inline-block text-orange-600 hover:text-orange-700">
            Gå til forsiden
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-stone-50 min-h-screen">
      {/* Hero Header */}
      <section className="relative h-[65vh] min-h-[500px] bg-stone-900 flex items-center justify-center overflow-hidden">
        <motion.img
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.6 }}
          transition={{ duration: 1.5 }}
          src={category.image}
          alt={category.category}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-linear-to-t from-stone-900/80 to-transparent" />
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="relative z-10 text-center px-4 max-w-4xl"
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-white mb-4">
            {category.category}
          </h1>
        </motion.div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            
            {/* Treatment List */}
            <motion.div 
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="lg:col-span-2 space-y-8"
            >
              <h2 className="text-3xl text-stone-900 mb-8">
                {category.id === "tillaeg" ? "Mine ydelser" : "Mine behandlinger"}
              </h2>
              <div className="grid grid-cols-1 gap-6">
                {category.items.map((item, index) => (
                  <motion.div 
                    key={index} 
                    variants={fadeIn}
                    className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-stone-100 hover:shadow-md transition-shadow group"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                      <h3 className="text-xl text-stone-900 group-hover:text-orange-900 transition-colors">
                        {item.title}
                      </h3>
                      <span className="text-xl font-semibold text-orange-900 bg-orange-50 px-4 py-1 rounded-full border border-orange-100">
                        {item.price}
                      </span>
                    </div>
                    <p className="text-stone-600 leading-relaxed max-w-2xl">
                      {item.description}
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-stone-600 text-sm">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-orange-600 flex-shrink-0" />
                        <span>Altid inklusiv professionel vejledning</span>
                      </div>
                      {item.subOption && (
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-orange-600 flex-shrink-0" />
                          <span>{item.subOption.title}: <span className="font-semibold text-orange-900">{item.subOption.price}</span></span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Sidebar / CTA */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="lg:col-span-1"
            >
              <div className="sticky top-32 space-y-6">
                {/* Bestil tid Box */}
                <div className="bg-white rounded-2xl p-6 md:p-8 shadow-md border border-stone-100 relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-brand-medium to-brand-dark" />
                  
                  <h3 className="text-2xl text-stone-900 mb-3 font-light">Bestil tid i dag</h3>
                  <p className="text-stone-600 text-sm mb-6 leading-relaxed">
                    Kontakt mig for at bestille din næste behandling eller hvis du har spørgsmål.
                  </p>
                  
                  <div className="space-y-3">
                    <a
                      href={`tel:${contactInfo.phone}`}
                      className="flex items-center justify-center gap-3 w-full rounded-xl bg-brand-dark py-3.5 text-brand-cream font-semibold hover:bg-brand-medium transition-all active:scale-98 shadow-sm hover:shadow-md"
                    >
                      <i className="fa-solid fa-phone text-sm" />
                      Ring {contactInfo.phoneDisplay}
                    </a>
                    <Link
                      to="/kontakt"
                      className="flex items-center justify-center gap-2 w-full rounded-xl border-2 border-stone-200 py-3 text-stone-800 font-semibold hover:bg-stone-50 hover:border-stone-400 transition-all active:scale-98"
                    >
                      <i className="fa-regular fa-envelope text-sm" />
                      Send en besked
                    </Link>
                  </div>

                  <div className="mt-6 pt-6 border-t border-stone-100 space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-orange-50 rounded-lg text-orange-700 shrink-0 mt-0.5">
                        <i className="fa-solid fa-location-dot text-xs" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-stone-700">Område</p>
                        <p className="text-xs text-stone-500 mt-0.5">{contactInfo.area}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-orange-50 rounded-lg text-orange-700 shrink-0 mt-0.5">
                        <i className="fa-solid fa-clock text-xs" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-stone-700">Åbningstider</p>
                        <p className="text-xs text-stone-500 mt-0.5">{contactInfo.hours}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-orange-50 rounded-lg text-orange-700 shrink-0 mt-0.5">
                        <i className="fa-solid fa-shield-halved text-xs" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-stone-700">Samtykke & Sikkerhed</p>
                        <p className="text-xs text-stone-500 leading-relaxed mt-0.5">
                          Alle kunder underskriver en samtykkeerklæring før første behandling (opbevares sikkert og fortroligt).
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Kørselsgebyr */}
                <div className="bg-brand-cream rounded-2xl p-6 border border-brand-cream/20 shadow-sm relative overflow-hidden">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-2 bg-brand-dark rounded-xl text-brand-cream shrink-0">
                      <i className="fa-solid fa-car text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-base font-medium text-brand-dark">Kørselsgebyr</h4>
                      <p className="text-stone-800 text-xs leading-relaxed mt-0.5">
                        Da jeg tilbyder udekørende behandling i dit eget hjem, tillægges der et mindre kørselsgebyr pr. behandling.
                      </p>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <div className="bg-white/80 backdrop-blur-xs px-3 py-2.5 rounded-xl border border-brand-medium/10 flex justify-between items-center gap-2 text-xs">
                      <span className="text-stone-800 font-medium">Nærområde: Kruså, Padborg, Faaborg, Millinge & Dyreborg</span>
                      <span className="font-bold text-brand-dark bg-brand-cream px-2 py-0.5 rounded-md border border-brand-medium/20 shrink-0">75,-</span>
                    </div>
                    <div className="bg-white/80 backdrop-blur-xs px-3 py-2.5 rounded-xl border border-brand-medium/10 flex justify-between items-center gap-2 text-xs">
                      <span className="text-stone-800 font-medium">Øvrige områder: Gråsten, Broager, Egernsund, Tinglev m.fl.</span>
                      <span className="font-bold text-brand-dark bg-brand-cream px-2 py-0.5 rounded-md border border-brand-medium/20 shrink-0">100,-</span>
                    </div>
                  </div>
                </div>

                {/* Info Card */}
                <div className="bg-brand-dark rounded-2xl p-6 text-white shadow-md overflow-hidden relative border border-brand-dark/20">
                  <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-brand-medium/10 rounded-full blur-2xl" />
                  <div className="flex items-center gap-2.5 mb-3">
                    <i className="fa-solid fa-house-medical text-brand-cream text-sm" />
                    <h4 className="text-base font-medium text-brand-cream">Hjemmebehandling & klinik</h4>
                  </div>
                  <p className="text-stone-300 text-xs leading-relaxed relative z-10">
                    Jeg tilbyder behandling i mine 2 faste klinikker i Kollund (Kruså/Padborg) og Dyreborg (Faaborg/Millinge) eller som udekørende hjemmebesøg i begge områder. Uanset hvad du vælger, er fokus altid på din tryghed og fodsundhed.
                  </p>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  );
}
